import { useAdoptedQuery } from '@services/hooks/endusers/adoptedQuery';
import { useLeadUserQuery } from '@services/hooks/endusers/leadUserQuery';
import { useAllEndUserQuery } from '@services/hooks/endusers/allEndUserQuery';
import { useClubDropDownQuery } from '@services/hooks/endusers/clubListQuery';
import { useDealersQuery } from '@services/hooks/enduser-details/useDealerQuery';
import { useIMRCodeListQuery } from '@services/hooks/enduser-details/useIMRCodeListQuery';
import { useTownDetailsQuery } from '@services/hooks/enduser-details/useTownDetailsQuery';
import { useAvailableProductsListQuery } from '@services/hooks/site-leads/useAvailableProductsListQuery';
import { useMeetingsListQuery } from '@services/hooks/meetings/useMeetingsListQuery';
import { useMeetingsTypesQuery } from '@services/hooks/meetings/useMeetingTypesQuery';
import { useProfileQuery } from '@services/hooks/profile/useProfileQuery';
import { useSiteSummaryListing } from '@services/hooks/site-summary-listing';
import { useDealerVisitProducts, useDealerVisits } from '@services/hooks/dealerVisit';
import {
	useDealersListQuery,
	useEndUserCategoryList,
	useEndUserListingQuery,
	useGetOneToOneMeetingsList,
	useWssTerritoryListQuery,
} from '@services/hooks/oneToOneMeetings';
import { useBDEOnLeaveStore } from '@store/bdeOnLeaveStore';
import { QueryObserverResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
import { useSupervisorList } from '@services/hooks/supervisor';
import { useFactoryVisitList } from '@services/hooks/factoryVisit';
import { usePermissionCheckForApiCallback } from '../authGuard';
import { useFetchSiteSpecialist } from '@services/hooks/site-specialist';
import { useAvailableOtherProductsListQuery } from '@services/hooks/site-leads/useAvailableOtherProductsListQuery';
import { useCMDIDropdownRBDMQuery } from '@services/hooks/endusers/useCMDIDropdownRBDMQuery';
import { useMeetingListRBDM } from '@services/hooks/meetings/useMeetingListRBDM';
import { useSiteListQuery } from '@services/hooks/lms/siteList';
import { useLmsUserLeadsQuery } from '@services/hooks/lms/userLeads';
import { useRbdmMeetCompare } from '@services/hooks/meetings/useMeetCompare';
import { useMdpMeetingListQuery } from '@services/hooks/meetings/useMdpMeetingListQuery';
import { useRigidAllUserQuery } from '@services/hooks/endusers/rigitAllUser';

export const UseSyncData = (): void => {
	useAdoptedQuery();
	useLeadUserQuery();
	useAllEndUserQuery();
	useClubDropDownQuery();
	useDealersQuery();
	useSiteSummaryListing();
	useCMDIDropdownRBDMQuery();
	useMdpMeetingListQuery();
};

interface IRefetchAllData {
	refetchAllData?: (refetchName?: string | string[]) => Promise<void>;
}

type RefetchFunction = () => Promise<QueryObserverResult>;

export const useRefetchAllData = (): IRefetchAllData => {
	const { refetch: refetchAdopted } = useAdoptedQuery();
	const { refetch: refetchLeadUser } = useLeadUserQuery();
	const { refetch: refetchAllEndUser } = useAllEndUserQuery();
	const { refetch: refetchClubDropDown } = useClubDropDownQuery();
	const { refetch: refetchDealer } = useDealersQuery();
	const { refetch: refetchImrCodeList } = useIMRCodeListQuery();
	const { refetch: refetchTowns } = useTownDetailsQuery();
	const { refetch: refetchProducts } = useAvailableProductsListQuery();
	const { refetch: refetchOtherProducts } = useAvailableOtherProductsListQuery();
	const { refetch: refetchTypes } = useMeetingsTypesQuery();
	const { refetch: refetchMeetings } = useMeetingsListQuery();
	const { refetch: refetchMeetingsRBDM } = useMeetingListRBDM();
	const { refetch: refetchSiteSummaryListing } = useSiteSummaryListing();
	const { refetch: refetchProfile } = useProfileQuery();
	const { refetch: refetchDealerVisits } = useDealerVisits();
	const { refetch: refetchDealerVisitProducts } = useDealerVisitProducts();
	const { refetch: refetchOneToOneMeetingList } = useGetOneToOneMeetingsList();
	const { refetch: refetchEndUserList } = useEndUserListingQuery();
	const { refetch: refetchWssTerritoryList } = useWssTerritoryListQuery();
	const { refetch: refetchPrimaryDealersList } = useDealersListQuery();
	const { refetch: refetchEndUserCategoryList } = useEndUserCategoryList();
	const { refetch: refetchSupervisorList } = useSupervisorList();
	const { refetch: refetchFactoryVisitList } = useFactoryVisitList();
	const { refetch: refetchLmsSiteList } = useSiteListQuery();
	const { refetch: refetchLmsLeadUserList } = useLmsUserLeadsQuery();
	const { refetch: refetchSiteSpecialistList } = useFetchSiteSpecialist();
	const { refetch: refetchRbdmMeetCompare } = useRbdmMeetCompare();
	const { refetch: refetchMdpMeetingList } = useMdpMeetingListQuery();
	const { refetch: refetchRigidUserList } = useRigidAllUserQuery();

	// Get permission check function
	const { permissionCheckForApi } = usePermissionCheckForApiCallback();

	// Only call useFetchSiteSpecialist if user has AddNewMicroSite permission
	const hasAddNewMicroSitePermission = permissionCheckForApi({ componentName: 'AddNewMicroSite' });
	const refetchMap: Record<string, RefetchFunction | undefined> = {
		EndUsers: refetchAdopted,
		Supervisor: refetchSupervisorList,
		SupervisorRBDM: refetchSupervisorList,
		AllEndUser: refetchAllEndUser,
		FactoryVisit: refetchFactoryVisitList,
		AllSites: refetchLmsSiteList,
		AllRigidUser: refetchRigidUserList,
	};
	const { isModalOpen } = useBDEOnLeaveStore();

	const nameWiseRefetchData = async (refetchNames: string | string[]): Promise<void> => {
		const names = Array.isArray(refetchNames) ? refetchNames : [refetchNames];

		const refetchFunctions = names.map((name) => refetchMap[name]).filter((fn): fn is RefetchFunction => !!fn);

		const invalidNames = names.filter((name) => !refetchMap[name]);
		if (invalidNames.length > 0) {
			// eslint-disable-next-line no-console
			console.warn(`No refetch functions found for names: ${invalidNames.join(', ')}`);
		}

		if (refetchFunctions.length > 0) {
			await Promise.all(refetchFunctions.map((fn) => fn()));
		}
	};

	const refetchAllData = async (refetchName: string | string[] = 'all'): Promise<void> => {
		if (isModalOpen) {
			await Promise.all([]);
		}
		if (refetchName === 'all') {
			await Promise.all([
				...(permissionCheckForApi({ componentName: 'EndUsers' }) ? [refetchAdopted?.()] : []),
				...(permissionCheckForApi({ componentName: 'NewLeads' }) ? [refetchLeadUser?.()] : []),
				refetchAllEndUser?.(),
				refetchMeetingsRBDM?.(),
				...(permissionCheckForApi({ componentName: 'Meetings' }) ? [refetchMeetings?.()] : []),
				...(permissionCheckForApi({ componentName: 'SiteSummary' }) ? [refetchSiteSummaryListing?.()] : []),
				refetchDealerVisitProducts?.(),
				...(permissionCheckForApi({ componentName: 'OneToOneMeetingSummary' }) ? [refetchOneToOneMeetingList?.()] : []),
				refetchTowns?.(),
				refetchDealer?.(),
				refetchProducts?.(),
				refetchOtherProducts?.(),
				refetchClubDropDown?.(),
				refetchImrCodeList?.(),
				...(permissionCheckForApi({ componentName: 'Meetings' }) ? [refetchTypes?.()] : []),
				...(permissionCheckForApi({ componentName: 'AddDealerVisit' }) ? [refetchDealerVisits?.()] : []),
				refetchEndUserList?.(),
				refetchWssTerritoryList?.(),
				refetchPrimaryDealersList?.(),
				refetchEndUserCategoryList?.(),
				refetchProfile?.(),
				refetchRbdmMeetCompare?.(),
				...(permissionCheckForApi({ componentName: 'Supervisor' }) ? [refetchSupervisorList?.()] : []),
				...(permissionCheckForApi({ componentName: 'FactoryVisit' }) ? [refetchFactoryVisitList?.()] : []),
				// Only refetch site specialist if user has permission
				...(hasAddNewMicroSitePermission ? [refetchSiteSpecialistList?.()] : []),
				...(permissionCheckForApi({ componentName: 'AllSites' }) ? [refetchLmsSiteList?.()] : []),
				...(permissionCheckForApi({ componentName: 'AllUsers' }) ? [refetchLmsLeadUserList?.()] : []),
				...(permissionCheckForApi({ componentName: 'MDPMeetings' }) ? [refetchMdpMeetingList?.()] : []),
				...(permissionCheckForApi({ componentName: 'AllRigidUser' }) ? [refetchRigidUserList?.()] : []),
			]);
		} else {
			await nameWiseRefetchData(refetchName);
		}
	};

	return { refetchAllData };
};

export const useRefetchList = (refetchName?: string | string[]): void => {
	const { setRefetchName } = useSiderToggleMenu();

	const memoizedRefetchName = useMemo(() => {
		if (!refetchName) return 'all';
		return Array.isArray(refetchName) ? refetchName.join('|') : refetchName;
	}, [refetchName]);

	useEffect(() => {
		setRefetchName(memoizedRefetchName.split('|'));

		return (): void => {
			setRefetchName('all');
		};
	}, [setRefetchName, memoizedRefetchName]);
};
